import Feature from 'ol/Feature.js';
import GeoJSON from 'ol/format/GeoJSON.js';
import Point from 'ol/geom/Point.js';
import VectorLayer from 'ol/layer/Vector.js';
import VectorSource from 'ol/source/Vector.js';
import Fill from 'ol/style/Fill.js';
import Stroke from 'ol/style/Stroke.js';
import Style from 'ol/style/Style.js';
import Text from 'ol/style/Text.js';

import { getLayerById } from './map/layer-tools'
import { map, zoomToLayer } from './map/tools'

let nord = 0;
let øst = 0;

const iconStyle = (feature, resolution) => {
  const point = feature.getGeometry().getType() === 'Point';
  const hnr = `Gårds-/bruksnr: \n${searchField.value}`;
  const xy = `\n${nord}° N ${øst}° Ø`;
  const s = [new Style({
    stroke: new Stroke({ color: '#FF9999', width: 3 }),
    text: new Text({
      font: '16px arial,sans-serif',
      text: point ? xy : hnr,
      stroke: new Stroke({ color: 'white', width: 3 }),
      textBaseline: point ? 'hanging' : 'middle'
    })
  })];
  if (point) s.push(new Style({
    text: new Text({
      font: '24px arial,sans-serif',
      text: '📍',
      textBaseline: 'bottom'
    })
  }));
  return s;
};

const goToBoundary = searchText => {
  const hnrText = searchText.replace(/ /g, '');
  // const currentProj = map.getView().getProjection().getCode().split(':')[1];
  // [ 3014-245/2 (32)  3014-245/2 (33)  5444-20/2 (35) ]
  const hnr = hnrText.split(/[-/]+/);
  // Kan inneholde mellomrom, tall, - og / med lengde 8-12
  const validGNRString = /^[0-9-/]{8,12}$/.test(hnrText);
  if (validGNRString && (hnr.length > 2) && hnr[2]) {
    const property = hnr[0] + ',' + hnr[1] + ',' + hnr[2] + ',' + (hnr[3] || 0);
    getPropertyBorder(property);
  } else searchField.setCustomValidity(
    'Vennligst legg inn eiendom med Kommunenummer-Gnr/Bnr'
  );
};

let controller = new AbortController();
const getPropertyBorder = property => {
  controller.abort();
  controller = new AbortController();
  const p = map.getView().getProjection().getCode();
  const url = '/matrikkel/' + property + ',' + p.split(':')[1];
  fetch(url, { signal: controller.signal }).then(r => r.json()).then(
    propertyJson => {
      if (propertyJson.features && propertyJson.features.length > 0)
        zoomToLayer(addBoundary(new GeoJSON().readFeatures(propertyJson)));
    }
  );
};

const addBoundary = boundary => {
  let b = getLayerById('boundary');
  if (b) {
    const s = b.getSource();
    s.clear();
    s.addFeatures(boundary);
  } else {
    const source = new VectorSource({ features: boundary });
    b  = new VectorLayer({ id: 'boundary', source, style: iconStyle, zIndex: 2 });
    map.addLayer(b);
  }
  return b;
};

const addMarker = point => {
  const iconFeature = new Feature({ geometry: point });
  iconFeature.setStyle(iconStyle);
  const m = getLayerById('marker');
  if (m) {
    const s = m.getSource();
    s.clear();
    s.addFeature(iconFeature);
  } else {
    const source = new VectorSource({ features: [iconFeature] });
    map.addLayer(new VectorLayer({ id: 'marker', source, zIndex: 2 }));
  }
};

const goToCoordinate = (north, east, epsg) => {
  const currentProj = map.getView().getProjection().getCode();
  nord = north.toLocaleString('no-NO', { maximumFractionDigits: 2 }); // marker label
  øst = east.toLocaleString('no-NO', { maximumFractionDigits: 2 }); // marker label
  const point = new Point([east, north]);
  // Transform input coordinates from their projection to map projection:
  addMarker(point.transform('EPSG:' + epsg, currentProj));
  const view = map.getView();
  const z = Math.max(view.getZoom(), 10); // closest
  setTimeout(() => { // needed to see the marker during animation
    view.animate({ center: point.getCoordinates(), zoom: z, duration: 1800 });
  }, 100);
};

const showResult = e => {
  const stringWithWhiteSpacesOnBothSides = e.target.value;
  const str = stringWithWhiteSpacesOnBothSides.trim();
  if (str.length === 0) { cleanSearchResult(); return; }
  const xmlhttpStedsnavn = new XMLHttpRequest();
  const xmlhttpMatrikkel = new XMLHttpRequest();
  xmlhttpStedsnavn.onreadystatechange = () => {
    if (xmlhttpStedsnavn.readyState === 4 && xmlhttpStedsnavn.status === 200) {
      steder.innerHTML = '';
      const response = JSON.parse(xmlhttpStedsnavn.responseText);
      const numberOfMatches = response.metadata.totaltAntallTreff;
      const matches = response.navn;
      if (numberOfMatches > 0) {
        matches.forEach(m => {
          const type = m.navneobjekttype;
          if (['Kommune', 'Fylke'].indexOf(type) < 0 && m.kommuner) {
            const municipality = m.kommuner[0].kommunenavn;
            const li = document.createElement('li');
            li.id = m.stedsnummer;
            li.innerHTML = m.skrivemåte + ', ' + type + ', ' + municipality;
            li.data = m.representasjonspunkt;
            li.onclick = () => goToPlace(li);
            li.onkeydown = e => {
              switch (e.key) {
                case 'Enter':
                  goToPlace(li);
                  break;
                case 'ArrowUp':
                  e.preventDefault(); // forhindrer scroll med pil
                  li.previousElementSibling?.focus();
                  break;
                case 'ArrowDown':
                  e.preventDefault(); // forhindrer scroll med pil
                  li.nextElementSibling?.focus();
                  break;
                default:
                  // Ingenting
              }
            };
            li.tabIndex = 0;
            steder.appendChild(li);
          }
        });
        searchResult.classList.add('steder');
      } else searchResult.classList.remove('steder');
    }
  };
  xmlhttpMatrikkel.onreadystatechange = () => {
    if (xmlhttpMatrikkel.readyState === 4 && xmlhttpMatrikkel.status === 200) {
      matrikkel.innerHTML = '';
      const response = JSON.parse(xmlhttpMatrikkel.responseText);
      if (response.length > 0) {
        response.forEach(match => {
          if (match.attrs.objektType === 'EIENDOM') {
            const isFarm = match.attrs.landbrukseiendom || false;
            const li = document.createElement('li');
            li.id = match.id;
            li.class = isFarm ? 'landbrukseiendom' : '';
            li.innerHTML = capitalizeFirstLetter(match.attrs.title);
            li.onclick = () => goToMatrikkel(li);
            li.onkeydown = e => {
              switch (e.key) {
                case 'Enter':
                  goToMatrikkel(li);
                  break;
                case 'ArrowUp':
                  e.preventDefault(); // forhindrer scroll med pil
                  li.previousElementSibling?.focus();
                  break;
                case 'ArrowDown':
                  e.preventDefault(); // forhindrer scroll med pil
                  li.nextElementSibling?.focus();
                  break;
                default:
                  // Ingenting
              }
            };
            li.tabIndex = 0;
            matrikkel.appendChild(li);
          }
        });
        if (matrikkel.children.length > 0)
          searchResult.classList.add('matrikkel');
        else searchResult.classList.remove('matrikkel');
      }
    }
  };
  const limit = 'treffPerSide=20';
  const k = 'utkoordsys=4258';
  const side = 'side=1';
  const fuzzy = 'fuzzy=true';
  const pattern = 'sok=' + str + '*';
  const places = `/stedsnavn/v1/navn?${fuzzy}&${side}&${limit}&${k}&${pattern}`;
  xmlhttpStedsnavn.open('GET', places);
  const matrikkelUrl = 'https://kilden.nibio.no/kilden_ws/rest/services/search';
  const cadastre = `${matrikkelUrl}?src=utm33&search=${str}`;
  xmlhttpMatrikkel.open('GET', cadastre);

  xmlhttpStedsnavn.send();
  xmlhttpMatrikkel.send();
};

const goToPlace = li => {
  searchField.value = li.innerHTML;
  searchField.focus();
  cleanSearchResult();
  const coord = li.data;
  goToCoordinate(coord.nord, coord.øst, coord.koordsys ?? 4258);
};

const goToMatrikkel = li => {
  const title = li.innerHTML;
  const validGNRString = /^[0-9-/]{8,14}$/.test(title);
  const gnr = validGNRString ? title : title.split('(')[1].split(')')[0];
  searchField.value = gnr;
  searchField.focus();
  cleanSearchResult();
  goToBoundary(gnr.replace(/ /g, ''));
};

// trigger from x button
const clearSearchField = () => {
  searchField.value = '';
  searchField.focus();
  cleanSearchResult();
  getLayerById('boundary')?.getSource().clear();
  getLayerById('marker')?.getSource().clear();
};

const cleanSearchResult = () => {
  searchResult.classList.remove('matrikkel', 'steder');
  steder.innerHTML = '';
  matrikkel.innerHTML = '';
};

const capitalizeFirstLetter = string => {
  const text = string.toLowerCase();
  return text.replace(/(^\w{1})|(\s{1}\w{1})/g, match => match.toUpperCase());
};

const goToFirstMatch = () => {
  if (steder.childNodes.length > 0) goToPlace(steder.firstChild);
  else if (matrikkel.childNodes.length > 0)
    goToMatrikkel(matrikkel.firstChild);
};

export const initializeSearch = () => {
  searchField.onchange = () => searchField.setCustomValidity('');
  searchField.oninput = showResult;
  searchField.onkeyup = e => {
    switch (e.key) {
      case 'Enter':
        goToFirstMatch();
        break;
      case 'ArrowUp':
        matrikkel.lastChild?.focus();
        break;
      case 'ArrowDown':
        steder.firstChild?.focus();
        break;
      default:
        // Ingenting
    }
  };

  search.onsubmit = () => { goToFirstMatch(); return false; };
  resetSearchBtn.onclick = clearSearchField;
};
